import { ActionIcon, Badge, Group, Stack, Tabs, Text } from "@mantine/core"
import { IconRefresh } from "@tabler/icons-react"
import { Loader } from "@tfg/ui"
import { isEmpty, partition } from "lodash"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useGenerateAlerts } from "~/gen/api"
import { DashboardAlerts } from "~/pages/Brand/Home/components/DashboardAlerts"
import DashboardTabContainer from "~/pages/Brand/Home/components/DashboardTabContainer"
import { EmailNotification } from "~/pages/Brand/Home/components/EmailNotification.tsx"
import { useListNotificationsQuery } from "~/pages/Brand/Home/hooks/useListNotificationsQuery.ts"
import { useRefreshNotificationsMutation } from "~/pages/Brand/Home/hooks/useRefreshNotificationsMutation.ts"
import { useAuth } from "~/store/auth-context"

type Tab = "pending" | "completed" | "alerts"

export const Notifications = () => {
  const { t } = useTranslation(["home"])
  const [activeTab, setActiveTab] = useState<Tab>("alerts")

  const { data: notificationsData, isLoading: isLoadingNotifications } =
    useListNotificationsQuery()

  const { mutate: refreshNotifications, isLoading } =
    useRefreshNotificationsMutation()

  const { user } = useAuth()

  const { data: alertsData, isLoading: isLoadingAlerts } = useGenerateAlerts(
    {
      organizationId: user?.organization?.id || 0,
    },
    {
      query: {
        enabled: Boolean(user?.organization?.id),
      },
    }
  )

  if (isLoadingNotifications) return <Loader fullPage />

  const [completedNotifications, pendingNotifications] = partition(
    notificationsData,
    (n) => n.archivedAt !== null
  )

  return (
    <Stack h="100%">
      <ActionIcon
        onClick={() => refreshNotifications()}
        loading={isLoading}
        style={{ position: "absolute", right: 20, top: 20 }}
      >
        <IconRefresh />
      </ActionIcon>
      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value as Tab)}
        h="100%"
      >
        <Tabs.List>
          <Tabs.Tab value="alerts">
            <Group>
              <Text fz="sm">{t("notifications.tab.alerts")}</Text>
              <Badge color="red" variant="filled" size="xs">
                {alertsData?.data.total}
              </Badge>
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="pending">
            <Group>
              <Text fz="sm">{t("notifications.tab.pending")}</Text>
              <Badge color="orange" variant="filled" size="xs" c="white">
                {pendingNotifications.length}
              </Badge>
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="completed">
            <Group>
              <Text fz="sm">{t("notifications.tab.completed")}</Text>
              <Badge color="green" variant="filled" size="xs">
                {completedNotifications.length}
              </Badge>
            </Group>
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="alerts">
          <DashboardTabContainer>
            <DashboardAlerts
              isLoading={isLoadingAlerts}
              dashboardAlerts={alertsData?.data}
              hasAlerts={Number(alertsData?.data.total) > 0}
            />
          </DashboardTabContainer>
        </Tabs.Panel>
        {["pending", "completed"].map((tab) => {
          const filteredNotifications =
            tab === "pending" ? pendingNotifications : completedNotifications
          return (
            <Tabs.Panel key={tab} value={tab} h="100%">
              <DashboardTabContainer>
                {isEmpty(filteredNotifications) ? (
                  <Group justify="center" gap="sm">
                    <Text fz="sm" style={{ textAlign: "center" }}>
                      {tab === "pending"
                        ? t("notifications.empty.pending")
                        : t("notifications.empty.completed")}
                    </Text>
                  </Group>
                ) : (
                  filteredNotifications.map((notification) => {
                    return (
                      <EmailNotification
                        key={notification.id}
                        notification={notification}
                      />
                    )
                  })
                )}
              </DashboardTabContainer>
            </Tabs.Panel>
          )
        })}
      </Tabs>
    </Stack>
  )
}
